import { useMemo } from 'react';

import { TFunction } from '@lyearn/i18n';
import {
  getPasteHandlerPlugin,
  getSelectionPlugins,
  markdownBreakToParagraphs,
  MarkType,
  MarkTypesEnum,
} from '@lyearn/lygo-blocks';
import { getMarkdownParser, getSchema } from '@lyearn/lygo-core';
import getEditorSchema from '@/components/PageEditor/schema';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

import { CourseLessonPage } from '../Extensions/@ly';

export const getSelectionAndPastePlguins = ({
  markdownParser,
  schema,
  CustomComponent,
  t,
}: {
  markdownParser: ReturnType<typeof getMarkdownParser>;
  schema: ReturnType<typeof getEditorSchema>['schema'];
  CustomComponent: MarkType['CustomComponent'];
  t: TFunction;
}) => {
  const customMarkItems = [
    {
      name: MarkTypesEnum.Custom,
      title: t('AI:AskAI'),
      CustomComponent,
    },
    { name: MarkTypesEnum.Break, break: 'line' },
  ];

  return [
    () => getPasteHandlerPlugin(markdownParser, schema),
    getSelectionPlugins(customMarkItems),
  ];
};

const useRichEditorProps = ({
  t,
  CustomComponent,
  extensions = [],
}: {
  t: TFunction;
  CustomComponent?: MarkType['CustomComponent'];
  extensions?: Parameters<typeof getSchema>[0];
}) => {
  const { [FeatureFlags.DefaultRestricted]: isDefaultRestricted } = useFeatureFlags();
  return useMemo(() => {
    const schemaElements = getEditorSchema([CourseLessonPage, ...extensions], {
      isDefaultRestricted,
    });
    const markdownParser = getMarkdownParser(schemaElements.schema, schemaElements.markdownTokens, [
      markdownBreakToParagraphs,
    ]);

    const selectionPlugins = getSelectionAndPastePlguins({
      markdownParser,
      schema: schemaElements.schema,
      CustomComponent,
      t,
    });

    return {
      ...schemaElements,
      markdownParser,
      plugins: [...schemaElements.plugins, ...selectionPlugins],
    };
  }, [CustomComponent, extensions, isDefaultRestricted, t]);
};

export default useRichEditorProps;
